import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { headers } from "../../../App/Axios"
import HTTPService from "../../../App/HTTPService"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { RootState } from "../../../App/store"
import { Room } from "../../../Types/Room"
import { RoleType } from "../../../App/enums"
import { TooltipQuestionmark } from "../../Shared/Components/SvgIcons"
import Tooltip from "../../Shared/Primitive/Tooltip"
import { CloseIcon } from "./SvgIcons"
import RoomPickerModal from "./RoomPickerModal"

function SearchableRoomDropdown(
    props: {
        itemId?: null | number
        hasTooltip?: false | boolean
        tooltipContent?: any
        title?: null | string
        validationError: null | string
        setItemId: Function
    } & React.HTMLAttributes<HTMLDivElement>
) {
    const [selected, setSelected] = useState("")
    const [isEmpty, setIsEmpty] = useState(true)
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const translation = useSelector(selectTranslations)
    const navigate = useNavigate()
    const { id: organizationId } = useParams()
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const [roomData, setRoomData] = useState<Array<Room>>([])
    const [isLoading, setIsLoading] = useState(true)

    const userRoomGetRequest = (id: number) => {
        return HTTPService.get(`/user/${id}/room/`, headers)
    }

    const organizationRoomGetRequest = (id: string) => {
        return HTTPService.get(`/organization/${id}/rooms/`, headers)
    }

    useEffect(() => {
        let isMounted = true

        if (role === RoleType.ORGANIZATION && organizationId) {
            organizationRoomGetRequest(organizationId)
                .then(response => {
                    if (isMounted && response.status === 200) {
                        setRoomData([...response.data.owned_rooms, ...response.data.joined_rooms])
                        setIsLoading(false)
                    }
                })
                .catch(error => {
                    console.log(error.response)
                })
        } else if (role === RoleType.USER && userDataState.id) {
            userRoomGetRequest(userDataState.id)
                .then(response => {
                    if (isMounted && response.status === 200) {
                        setRoomData(response.data.room)
                        setIsLoading(false)
                    }
                })
                .catch(error => {
                    console.log(error.response)
                })
        }

        return () => {
            isMounted = false
        }
    }, [navigate, userDataState, role, organizationId])

    const onRemoveClick = (e: (React.MouseEvent<HTMLDivElement, MouseEvent>)) => {
        e.stopPropagation();
        setSelected("")
        setIsEmpty(true)
        props.setItemId()
    }

    const onRoomClick = (name: string, id: number) => {
        setSelected(name)
        setIsEmpty(false)
        props.setItemId(id)
        setIsPickerOpen(false)
    }

    const hasAutoSelectedRef = useRef(false)

    useEffect(() => {
        if (hasAutoSelectedRef.current || isLoading || props.itemId) {
            return
        }

        const activeRooms = roomData.filter(room => room.is_active)
        if (activeRooms.length === 1) {
            hasAutoSelectedRef.current = true
            onRoomClick(activeRooms[0].name, activeRooms[0].id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomData, isLoading])

    return (
        <div className="room-picker-trigger-wrapper">
            {props.hasTooltip && (
                <div className="single-text-input__text">
                    {props.title}
                    <div>
                        <Tooltip direction="settings-top" content={props.tooltipContent}>
                            <TooltipQuestionmark />
                        </Tooltip>
                    </div>
                </div>
            )}
            <div className="room-picker-trigger" onClick={() => setIsPickerOpen(true)}>
                <span className="room-picker-trigger__label" title={selected || undefined}>
                    {selected || translation.chooseRoomModalTitle}
                </span>
                {!isEmpty && (
                    <span className="room-picker-trigger__clear" onClick={onRemoveClick}>
                        <CloseIcon />
                    </span>
                )}
            </div>

            <RoomPickerModal
                isOpen={isPickerOpen}
                title={translation.chooseRoomModalTitle}
                searchPlaceholder={translation.searchRoomPlaceholder}
                emptyStateText={translation.noRoomsFoundForSearch}
                rooms={roomData}
                selectedRoomId={props.itemId}
                onSelect={room => onRoomClick(room.name, room.id)}
                onClose={() => setIsPickerOpen(false)}
            />
        </div>
    )
}
export default SearchableRoomDropdown
